import {
  deleteFetcher,
  getFetcher,
  postFetcher,
  putFetcher,
} from '@/api/fetchers';
import { DropDownList } from '@/components/DropDownList/DropDownList';
import type { Tag, Tags } from '@/schemas/API/GET/tag/response';
import { Paper, Stack, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { IconTrash, IconEdit } from '@tabler/icons-react';
import { goodNotification } from '@/components/Notification/Notification';
import { FormModal } from '@/components/Modal/FormModal';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import {
  displayError,
  displayErrorOnUseState,
} from '@/utils/error/errorHandler';

export const TagsPage = (): JSX.Element => {
  const { data: tags } = useSWR<Tags>('/tags', getFetcher);

  const [isNewTagModalOpen, setNewTagModalOpen] = useState(false);
  const [isUpdateTagModalOpen, setUpdateTagModalOpen] = useState(false);
  const [isDeleteTagModalOpen, setIsDeleteTagModalOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [nameError, setNameError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
  });

  const { data: selectedTag } = useSWR<Tag>(
    selectedTagId == null ? null : `/tags/${selectedTagId}`,
    getFetcher
  ); // fetch data only if a tag is selected

  const closeModal = () => {
    mutate('/tags');
    setFormData({ name: '' });
    setNameError('');
  };

  const openDeleteTagModal = () => {
    setIsDeleteTagModalOpen(true);
  };
  const closeDeleteTagModal = () => {
    setIsDeleteTagModalOpen(false);
    closeModal();
  };
  const openNewTagModal = () => {
    setNewTagModalOpen(true);
  };
  const closeNewTagModal = () => {
    setNewTagModalOpen(false);
    closeModal();
  };

  const openUpdateTagModal = () => {
    setUpdateTagModalOpen(true);
  };
  const closeUpdateTagModal = () => {
    setUpdateTagModalOpen(false);
    closeModal();
  };

  async function deleteTag(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await deleteFetcher(`/tags/${selectedTagId}`);
      goodNotification('Úspěch!', 'Tag je smazan');
      closeDeleteTagModal();
    } catch (err) {
      displayError(err);
    }
  }

  async function updateTag(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNameError('');
    try {
      await putFetcher(`/tags/${selectedTagId}`, formData);
      goodNotification('Tag je upraven');
      closeUpdateTagModal();
    } catch (err) {
      displayErrorOnUseState(err, [{ field: 'name', setState: setNameError }]);
    }
  }

  async function createNewTag(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setNameError('');

    try {
      await postFetcher('/tags', formData);
      goodNotification('Úspěch!', 'Tag je vytvořen');
      closeNewTagModal();
    } catch (err) {
      displayErrorOnUseState(err, [{ field: 'name', setState: setNameError }]);
    }
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prevState => ({ ...prevState, [name]: value })); // without this a user won't see what he/she is typing
  };

  useEffect(() => {
    if (selectedTag !== undefined) {
      setFormData({ name: selectedTag.name });
    }
  }, [selectedTag]); // provide data about a selected tag when it is fetched

  return (
    <Stack className="h-full p-8 overflow-auto">
      <PageHeader name="Tagy" />

      <Paper withBorder radius="md" className="p-4 flex-1 overflow-auto">
        <DropDownList
          title={'Seznam tagů'}
          list={tags?.content}
          createNewItem={openNewTagModal}
          createNewItemButtonText="Nový tag"
          itemToolBar={[
            {
              index: 0,
              function: (item: Tag) => {
                setSelectedTagId(item.id);
                openUpdateTagModal();
              },
              icon: <IconEdit />,
              onHoverColor: 'blue',
            },
            {
              index: 1,
              function: (item: Tag) => {
                openDeleteTagModal();
                setSelectedTagId(item.id);
              },
              icon: <IconTrash />,
            },
          ]}
        ></DropDownList>
      </Paper>
      <FormModal
        confirmButtonColor="green"
        opened={isNewTagModalOpen}
        onClose={closeNewTagModal}
        onConfirm={createNewTag}
        title={'Nový tag'}
      >
        <TextInput
          name="name"
          variant="filled"
          label="Název tagu"
          size="md"
          radius="md"
          value={formData.name}
          onChange={handleChange}
          error={nameError}
          withErrorStyles={false}
        />
      </FormModal>
      <FormModal
        opened={isUpdateTagModalOpen}
        onClose={closeUpdateTagModal}
        onConfirm={updateTag}
        title={'Upavit tag'}
      >
        <TextInput
          name="name"
          variant="filled"
          label="Název tagu"
          size="md"
          radius="md"
          error={nameError}
          onChange={handleChange}
          value={formData.name}
          withErrorStyles={false}
        />
      </FormModal>
      <FormModal
        confirmButtonColor="red"
        opened={isDeleteTagModalOpen}
        onClose={closeDeleteTagModal}
        onConfirm={deleteTag}
        confirmText="Smazat"
      >
        <div className="text-lg text-center">
          Opravdu si přejete smazat tag{' '}
          <span className="font-bold italic">{selectedTag?.name}</span> ?
        </div>
      </FormModal>
    </Stack>
  );
};

export {};
